/**
 * The imports list's "N to review" pill, next to the filename.
 *
 * IT COUNTS THE SAME POPULATION THE DETAIL'S `To review` CHIP FILTERS, and
 * says so in the same words. It used to read "N conflicts" over a different
 * number — `dedup_status.w1_count + w2_count`, which omitted W3, omitted the
 * duplicate-LinkedIn warning, and (being a run SNAPSHOT) kept counting rows the
 * recruiter had since ignored or exported. On the operator's own data the pill
 * said 5 where the detail said 6, and on two neighbouring imports 5 vs 8 and
 * 3 vs 5.
 *
 * The wording moved with the number, deliberately. Even once two surfaces agree
 * arithmetically, two NAMES for one population invite the next reader to assume
 * they are different things — and "conflict" now means something narrower
 * elsewhere in this flow (the compare modal's `Needs your decision` cards, as
 * against a company change or a duplicate warning). "To review" is the
 * operator's own framing and the one the chip already carries.
 *
 * The count is a RESPONSE field. This component only RENDERS it; it never
 * computes the math. Until Compare has run it is `0` and no pill renders — which
 * is also why its absence cannot be read as "nothing to review", only as
 * "nothing counted yet".
 */
import { AlertTriangle } from "lucide-react";

export interface ConflictPillProps {
  count: number;
}

/**
 * Ink and glyph follow `to_review`, not the `conflict` family this pill used to
 * carry. Once the number and the words became the detail's `To review`
 * population, keeping a third visual language for it was the same defect one
 * layer out: a reader who learns amber+triangle means "needs your decision" on
 * the detail should not meet the same fact in a different colour on the list
 * they clicked through from.
 *
 * `text-warning-ink` rather than `text-warning` per `tokens.css`: `-ink` is the
 * text token, the base token is for fills and borders — which is exactly how
 * `bg-warning-muted` and `border-warning-strong` use it here.
 */
export function ConflictPill({ count }: ConflictPillProps) {
  if (!count || count <= 0) return null;
  return (
    <span
      data-testid="conflict-pill"
      title="Contacts that need a decision before this list is added to Thrive."
      className="inline-flex items-center gap-1 rounded-full bg-warning-muted text-warning-ink border border-warning-strong px-2 py-0.5 text-micro font-bold"
    >
      <AlertTriangle aria-hidden="true" className="size-[1em]" />
      {count.toLocaleString()} to review
    </span>
  );
}
